import { ReactComponent as RecorderSVG } from "assets/icons/sidenav/recorder.svg";
import { ReactComponent as LocationSVG } from "assets/icons/map-pin.svg";
import { ReactComponent as UserSVG } from "assets/icons/user-white.svg";
import { ReactComponent as CameraSVG } from "assets/icons/camera.svg";
import { ReactComponent as UserGroupSVG } from "assets/icons/users-white.svg";
import { ReactComponent as ReportSVG } from "assets/icons/sidenav/reports.svg";
import { ReactComponent as AlertSVG } from "assets/icons/sidenav/alerts.svg";
import { ReactComponent as AccountSVG } from "assets/icons/sidenav/account.svg";
import { ReactComponent as VideoSVG } from "assets/icons/sidenav/videoshare.svg";

import {
  AssigneeInfo,
  DashboardAlertData,
  DashboardFilterResult,
  MapSearchData,
  MarkerDataSystem,
} from "api/interfaces/dashboardInterface.interface";
import {
  AlertStatusCountType,
  clusterAlert,
  CustomMarker,
  MarkerDataType,
} from "pages/dashboard/map/GoogleMapContainer";
import { chain } from "lodash";
import { TreeDataNode } from "antd";

export const SEARCH_TYPE = {
  RECORDER: { name: "recorder", value: 0 },
  RECORDERGROUP: { name: "recorderGroup", value: 8 },
  LOCATION: { name: "location", value: 1 },
  CAMERA: { name: "camera", value: 4 },
  ALERT: { name: "alert", value: 2 },
  REPORT: { name: "report", value: 3 },
  USER: { name: "user", value: 5 },
  USERGROUP: { name: "userGroup", value: 6 },
  ACCOUNT: { name: "account", value: 7 },
  VIDEOSHARE: { name: "videoShare", value: 9 },
};

// 아이콘 타입과 해당 SVG 컴포넌트의 매핑
const iconComponents: { [key: string]: JSX.Element } = {
  recorder: <RecorderSVG />,
  recorderGroup: <RecorderSVG />,
  location: <LocationSVG />,
  camera: <CameraSVG />,
  alert: <AlertSVG />,
  report: <ReportSVG />,
  videoShare: <VideoSVG />,
  user: <UserSVG />,
  userGroup: <UserGroupSVG />,
  account: <AccountSVG />,
};

export const changeTypeToIcon = (type: string): JSX.Element | null => {
  // 해당하는 아이콘이 있는지 확인하고 반환
  return iconComponents[type] || null;
};

export const caluclateAlertStatus = (
  systems: MapSearchData[]
): AlertStatusCountType => {
  let alertCount = 0;
  let assignCount = 0;
  let criticalCount = 0;
  let warningCount = 0;

  systems.reduce((acc, cur) => {
    alertCount += cur.alerts.length;
    // assignCount += cur.assign.length;
    criticalCount += cur.critical.length;
    warningCount += cur.warnings.length;
    return acc;
  }, {});

  return {
    alertCount,
    // assignCount,
    criticalCount,
    warningCount,
  };
};

export const getAssignee = (
  alerts: DashboardAlertData[]
): AssigneeInfo | undefined => {
  const merged: AssigneeInfo = {};

  alerts.forEach((a) => {
    if (a.assigneeEmail && !merged.assigneeEmail) {
      merged.assigneeEmail = a.assigneeEmail;
    }
    if (a.assigneeName && !merged.assigneeName) {
      merged.assigneeName = a.assigneeName;
    }
    if (a.assigneePhone && !merged.assigneePhone) {
      merged.assigneePhone = a.assigneePhone;
    }
    if (a.assigneeId && !merged.assigneeId) {
      merged.assigneeId = a.assigneeId;
    }
    if (a.notifySms && !merged.notifySms) {
      merged.notifySms = a.notifySms;
    }
  });

  return Object.keys(merged).length > 0 ? merged : undefined;
};

export const filteringRecorder = (
  recorder: DashboardFilterResult
): MapSearchData => {
  const tempWarnings: DashboardAlertData[] = [];
  const tempAlerts: DashboardAlertData[] = [];
  const tempCritical: DashboardAlertData[] = [];

  const processAlerts = (
    alerts: DashboardAlertData[] | undefined,
    isWarning = false
  ) => {
    if (!alerts) return;

    alerts.forEach((alert) => {
      if (isWarning) {
        tempWarnings.push(alert);
      } else {
        if (alert.isCriticalSeverity) {
          tempCritical.push(alert);
        } else {
          tempAlerts.push(alert);
        }
      }
    });
  };

  processAlerts(recorder.warnings, true);
  processAlerts(recorder.alerts);

  const tempRecorder: MapSearchData = {
    ...recorder,
    locationAddress: recorder.location?.location || "",
    latitude: recorder.location?.latitude || 0,
    longitude: recorder.location?.longitude || 0,
    thumbnail: recorder.customMapUrl || recorder.thumbnailUrl,
    warnings: tempWarnings,
    alerts: tempAlerts,
    critical: tempCritical,
    isAssignee: getAssignee(recorder.alerts || []),
    // assign: [],
  };

  return tempRecorder;
};

export const convertToMapSearchData = (
  data: DashboardFilterResult[]
): MapSearchData[] => {
  return data.map((recorder: DashboardFilterResult) =>
    filteringRecorder(recorder)
  );
};

/**
클러스터 정보 창 계산
@param clusterMarkers google map 에서 제공하는 clusterMarker 의 marker
@returns calculateAlertStatus: clusterAlert; totalRecorder: cluster 포함 system 개수 표시
*/

export const getCalculateAlertStatus = (
  clusterMarkers: CustomMarker[] | null
): {
  calculateAlertStatus: clusterAlert;
  totalRecorder: number;
  recorderIds: string;
} => {
  let totalRecorder: number = 0;
  let calculateAlertStatus: clusterAlert = {
    alertStatusCount: {
      warningCount: 0,
      alertCount: 0,
      // assignCount: 0,
      criticalCount: 0,
    },
    alertType: {
      system: 0,
      disk: 0,
      video: 0,
    },
  };

  // console.log(clusterMarkers);
  let recorderIds: string = "";
  if (clusterMarkers) {
    /**
     * clusterer 에 포함된 recorderId : 로 조인 -> 애니메이션 효과 위함
     */
    recorderIds = clusterMarkers
      .filter((filterMarker) => filterMarker.data)
      .map((data) => data.data?.recorderIds)
      .join(":");

    calculateAlertStatus = clusterMarkers.reduce<clusterAlert>(
      (previousValue, currentValue, currentIndex) => {
        const subRecorder: MarkerDataSystem[] = (currentValue as any).data
          .groupingSystems;

        let warningCnt = 0;
        let alertCnt = 0;
        let assignCnt = 0;
        let criticalCnt = 0;
        let alertTypeCount = {
          system: 0,
          disk: 0,
          video: 0,
        };

        if (subRecorder !== undefined && subRecorder.length > 0) {
          totalRecorder += subRecorder.length;
          subRecorder.forEach((value, index) => {
            warningCnt += value.warnings?.length ?? 0;
            alertCnt += value.alerts?.length ?? 0;
            // assignCnt += value.assign?.length ?? 0;
            criticalCnt += value.critical?.length ?? 0;
            alertTypeCount.system += value.systemAlerts ?? 0;
            alertTypeCount.disk += value.storageAlerts ?? 0;
            alertTypeCount.video += value.videoAlerts ?? 0;
          });
        }

        return {
          alertStatusCount: {
            alertCount: previousValue.alertStatusCount.alertCount + alertCnt,
            warningCount:
              previousValue.alertStatusCount.warningCount + warningCnt,
            // assignCount: previousValue.alertStatusCount.assignCount + assignCnt,
            criticalCount:
              previousValue.alertStatusCount.criticalCount + criticalCnt,
          },
          alertType: {
            system: previousValue.alertType.system + alertTypeCount.system,
            disk: previousValue.alertType.disk + alertTypeCount.disk,
            video: previousValue.alertType.video + alertTypeCount.video,
          },
        };
      },
      {
        alertStatusCount: {
          warningCount: 0,
          alertCount: 0,
          // assignCount: 0,
          criticalCount: 0,
        },
        alertType: {
          system: 0,
          disk: 0,
          video: 0,
        },
      }
    );
  }
  return {
    totalRecorder,
    calculateAlertStatus,
    recorderIds,
  };
};

// merged 됐거나 같은 위치일때
export const checkingMergedSystem = (systems: MarkerDataSystem[]) => {
  return (
    systems.length > 1 ||
    systems.some((sys) => sys.recorders && sys.recorders.length > 0)
  );
};

/**
 *
 * @param recorders MarkerDataSystem[] : filter/recorders response data
 * @returns MarkerDataTypep[] : 지도 marker 데이터 구조에 맞게 맵핑
 * @description
 *  1) 위,경도 같은 것 끼리 그룹화 : latitude-logitude 로 비교
 *  2) mergedSystem 끼리 그룹화 : mergedSystemId 로 비교
 */
export const convertToMarkerGroupingData = (
  recorders: MapSearchData[]
): MarkerDataType[] => {
  return chain(recorders)
    .groupBy((recorder) => `${recorder.latitude}-${recorder.longitude}`)
    .values()
    .map((sameLocationGroup) => {
      if (!sameLocationGroup.length) return null;

      const { latitude, longitude } = sameLocationGroup[0];
      const mergedSystemGroup = chain(sameLocationGroup)
        .groupBy((recorder) => recorder.mergedSystemId)
        .value();

      const groupingSystems: MarkerDataSystem[] = [];
      let isMergedLocationSystem = false;
      let recorderIdsArr: string[] = [];

      Object.values(mergedSystemGroup).forEach((group: MapSearchData[]) => {
        if (group.length > 1) {
          isMergedLocationSystem = true;
          groupingSystems.push({
            ...group[0],
            recorders: group,
          });
          recorderIdsArr.push(...group.map((r) => r.recorderId));
        } else {
          groupingSystems.push(group[0]);
          recorderIdsArr.push(group[0].recorderId);
        }
      });

      const mergedSystemId = sameLocationGroup[0].mergedSystemId;
      const mergedSystemName = sameLocationGroup[0].mergedSystemName;

      const recorderIds = recorderIdsArr.join(":");

      return {
        latitude,
        longitude,
        mergedSystemId,
        recorderIds,
        title: mergedSystemName,
        groupingSystems,
        alertStatusCount: caluclateAlertStatus(sameLocationGroup),
      } as MarkerDataType;
    })
    .filter((item): item is MarkerDataType => item !== null)
    .value();
};

export const collectKeys = (nodes: TreeDataNode[]): React.Key[] =>
  nodes.flatMap((n) => [n.key, ...collectKeys(n.children ?? [])]);

export const getVisibleChildKeys = (
  nodes: TreeDataNode[],
  key: React.Key
): React.Key[] => {
  const find = (list: TreeDataNode[]): TreeDataNode | null => {
    for (const n of list) {
      if (n.key === key) return n;
      const visibleChildKeys = find(n.children ?? []);
      if (visibleChildKeys) return visibleChildKeys;
    }
    return null;
  };
  return collectKeys(find(nodes)?.children ?? []);
};

export const getTreeCheckState = (
  nodes: TreeDataNode[],
  selected: React.Key[]
) => {
  let checked = [...selected];
  const halfChecked: React.Key[] = [];

  const visit = (list: TreeDataNode[]) =>
    list.forEach((n) => {
      if (!n.children?.length) return;
      const desc = getVisibleChildKeys(nodes, n.key);
      const nSel = desc.filter((k) => selected.includes(k)).length;
      if (nSel > 0 && nSel < desc.length) {
        halfChecked.push(n.key);
        checked = checked.filter((k) => k !== n.key);
      } else if (nSel === desc.length && desc.length) {
        if (!checked.includes(n.key)) checked.push(n.key);
      } else {
        checked = checked.filter((k) => k !== n.key);
      }
      visit(n.children);
    });
  visit(nodes);
  return { checked, halfChecked };
};
